export type RideStatus = 'pending' | 'accepted' | 'driver_departed' | 'driver_arrived' | 'in_progress' | 'completed' | 'cancelled'
export type ClientStatus = 'active' | 'suspended' | 'unverified'
export type DriverStatus = 'online' | 'offline' | 'busy' | 'suspended'
export type PaymentStatus = 'completed' | 'pending' | 'failed' | 'refunded'

type UnknownRecord = Record<string, unknown>

export interface PaginationMeta {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

export interface AdminRide {
  id: string
  /** Identifiant technique (clé primaire) : utilisé pour les canaux temps réel. */
  numericId: number | null
  status: RideStatus
  client: string
  clientPhone: string | null
  driver: string | null
  driverPhone: string | null
  from: string
  to: string
  amount: number
  vehicle: string
  payment: string
  distanceKm: number
  durationMin: number
  createdAt: string
  rating: number | null
}

export interface RidePayment {
  id: string
  amount: number
  method: string
  status: string
  reference: string | null
  createdAt: string
}

export interface AdminRideDetail extends AdminRide {
  pickupLat: number | null
  pickupLng: number | null
  dropoffLat: number | null
  dropoffLng: number | null
  estimatedPrice: number
  finalPrice: number
  /** Frais d'annulation (crédit d'annulation) reportés sur cette course. */
  cancellationCredit: number
  driverRating: number | null
  clientRating: number | null
  startedAt: string | null
  completedAt: string | null
  cancelledAt: string | null
  cancelReason: string | null
  cancelledBy: string | null
  payments: RidePayment[]
}

export interface AdminClient {
  id: string
  name: string
  phone: string
  email: string | null
  avatar: string | null
  status: ClientStatus
  walletBalance: number
  totalRides: number
  totalSpent: number
  createdAt: string
  lastRideAt: string | null
}

export interface AdminDriver {
  id: string
  name: string
  phone: string
  email: string | null
  avatar: string | null
  status: DriverStatus
  vehicleType: string
  vehicleMake: string | null
  vehiclePlate: string
  rating: number
  totalRides: number
  earnings: number
  commission: number
  isVerified: boolean
  createdAt: string
  lastRideAt: string | null
}

/** Course récente affichée dans le dossier d'un chauffeur. */
export interface AdminDriverRide {
  id: string
  status: RideStatus
  client: string
  clientPhone: string | null
  from: string
  to: string
  amount: number
  distanceKm: number
  durationMin: number
  payment: string
  clientRating: number | null
  createdAt: string
  completedAt: string | null
}

/** Versement récent affiché dans le dossier d'un chauffeur. */
export interface AdminDriverPayout {
  id: string
  amount: number
  method: string
  methodLabel: string
  status: string
  statusLabel: string
  reference: string | null
  createdAt: string
  processedAt: string | null
}

/** Avis reçu par le chauffeur. */
export interface AdminDriverRating {
  id: string
  score: number
  comment: string | null
  raterName: string | null
  createdAt: string
}

export interface AdminDriverDetailProfile {
  id: string
  licenseNumber: string
  vehicleType: string
  vehicleMake: string
  vehicleModel: string
  vehicleYear: number
  vehicleColor: string
  vehiclePlate: string
  status: DriverStatus
  isVerified: boolean
  totalEarnings: number
  createdAt: string
  updatedAt: string | null
}

export interface AdminDriverDetailPerformance {
  rating: number
  ratingCount: number
  totalRides: number
  completedRides: number
  cancelledRides: number
  inProgressRides: number
  upcomingRides: number
  completionRate: number
  cancellationRate: number
  totalDistanceKm: number
  totalDurationMin: number
  averageFare: number
  firstRideAt: string | null
  lastRideAt: string | null
}

export interface AdminDriverDetailPayouts {
  total: number
  completed: number
  pending: number
  processing: number
  failed: number
  totalPaidOut: number
  pendingAmount: number
  lastPayoutAt: string | null
}

export interface AdminDriverDetailFinance {
  walletBalance: number
  cancellationCredit: number
  grossRevenue: number
  netEarnings: number
  commission: number
  commissionRate: number
  payouts: AdminDriverDetailPayouts
  recentPayouts: AdminDriverPayout[]
}

export interface AdminDriverSubscription {
  id: string
  plan: string | null
  status: string
  startsAt: string | null
  endsAt: string | null
  freeRidesRemaining: number
  isActive: boolean
}

/** Dossier chauffeur complet (GET /admin/drivers/{user}). */
export interface AdminDriverDetail {
  id: string
  /** Identifiant technique du compte : utilisé pour les canaux temps réel. */
  numericId: number | null
  name: string
  phone: string
  email: string | null
  avatar: string | null
  roles: string[]
  accountStatus: 'active' | 'inactive' | 'suspended'
  isActive: boolean
  isBackOffice: boolean
  createdAt: string
  updatedAt: string | null
  profile: AdminDriverDetailProfile | null
  location: {
    latitude: number | null
    longitude: number | null
    lastUpdatedAt: string | null
  }
  performance: AdminDriverDetailPerformance
  finance: AdminDriverDetailFinance
  subscription: AdminDriverSubscription | null
  recentRides: AdminDriverRide[]
  recentRatings: AdminDriverRating[]
}

export interface FinanceOverviewData {
  totalRevenue: number
  revenueGrowth: number
  totalCommissions: number
  commissionsGrowth: number
  pendingPayouts: number
  walletBalance: number
  byMethod: Array<{ name: string; value: number }>
}

export interface FinanceTransaction {
  id: string
  type: 'ride_payment'
  amount: number
  userId: string | null
  userName: string
  userRole: 'client' | 'driver' | 'admin' | 'superadmin' | 'unknown'
  description: string
  status: PaymentStatus
  createdAt: string
  reference: string
  method: string
  rideId: string | null
}

export interface PendingPayout {
  id: string
  name: string
  phone: string
  amount: number
  totalRides: number
  isVerified: boolean
  status: string
}

export interface ReportStats {
  totalRides: number
  totalRevenue: number
  totalDrivers: number
  totalUsers: number
  avgRating: number
  cancelRate: number
}

export interface ReportPoint {
  date: string
  name: string
  rides: number
  revenue: number
}

export interface ReportSlice {
  name: string
  value: number
}

export interface ZoneMetric extends ReportSlice {
  revenue: number
  avgDistanceKm: number
  avgDurationMin: number
  cancelRate: number
}

export interface OverviewReport {
  stats: ReportStats
  daily: ReportPoint[]
  ridesByType: ReportSlice[]
  topZones: ZoneMetric[]
  hourly: ReportSlice[]
}

export interface RevenuePoint {
  date: string
  rides: number
  revenue: number
  commission: number
}

export interface RevenueReport {
  totalRevenue: number
  totalCommission: number
  netRevenue: number
  daily: RevenuePoint[]
  byMethod: Array<{ method: string; label: string; total: number; count: number }>
}

export interface DriverReportItem {
  id: number
  name: string
  phone: string
  status: DriverStatus
  completedRides: number
  totalEarnings: number
  isVerified: boolean
  vehicleType: string | null
  createdAt: string
}

export interface DriverReport {
  totalDrivers: number
  activeDrivers: number
  inactiveDrivers: number
  topDrivers: DriverReportItem[]
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asRecord(value: unknown): UnknownRecord {
  return isRecord(value) ? value : {}
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

function toStringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function toNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function toBoolean(value: unknown): boolean {
  return value === true || value === 1 || value === '1'
}

function normalizeRideStatus(value: unknown): RideStatus {
  const status = toStringValue(value, 'pending')

  if (
    status === 'accepted' ||
    status === 'driver_departed' ||
    status === 'driver_arrived' ||
    status === 'in_progress' ||
    status === 'completed' ||
    status === 'cancelled'
  ) {
    return status
  }

  return 'pending'
}

function normalizeClientStatus(value: unknown): ClientStatus {
  const status = toStringValue(value, 'active')

  if (status === 'suspended') return 'suspended'
  if (status === 'inactive' || status === 'unverified') return 'unverified'
  return 'active'
}

export function normalizeDriverStatus(value: unknown, fallbackStatus?: unknown): DriverStatus {
  // Une suspension de COMPTE (users.status = 'suspended') prime sur la
  // disponibilité du profil : à la suspension le backend bascule le profil
  // à « offline ». Sans cette priorité, le chauffeur suspendu s'afficherait
  // « Hors ligne » et le bouton de réactivation n'apparaîtrait jamais.
  if (toStringValue(fallbackStatus, '') === 'suspended') return 'suspended'

  const status = toStringValue(value || fallbackStatus, 'offline')

  if (status === 'suspended') return 'suspended'
  if (status === 'busy') return 'busy'
  if (status === 'online' || status === 'available') return 'online'
  return 'offline'
}

function normalizePaymentStatus(value: unknown): PaymentStatus {
  const status = toStringValue(value, 'pending')

  if (status === 'completed' || status === 'failed' || status === 'refunded') {
    return status
  }

  return 'pending'
}

export function mapPaginationMeta(value: unknown): PaginationMeta {
  const record = asRecord(value)

  return {
    currentPage: toNumber(record.current_page ?? record.currentPage, 1),
    lastPage: toNumber(record.last_page ?? record.lastPage, 1),
    perPage: toNumber(record.per_page ?? record.perPage, 20),
    total: toNumber(record.total, 0),
  }
}

export function mapAdminRide(value: unknown): AdminRide {
  const record = asRecord(value)
  const client = asRecord(record.client)
  const driver = asRecord(record.driver)
  const pickup = asRecord(record.pickup)
  const dropoff = asRecord(record.dropoff)

  return {
    id: toStringValue(record.id),
    numericId: record.numeric_id === undefined ? null : toNumber(record.numeric_id),
    status: normalizeRideStatus(record.status),
    client: toStringValue(client.name ?? record.client_name, 'Client inconnu'),
    clientPhone: toNullableString(client.phone),
    driver: toNullableString(driver.name ?? record.driver_name),
    driverPhone: toNullableString(driver.phone),
    from: toStringValue(record.from ?? pickup.address, 'Départ inconnu'),
    to: toStringValue(record.to ?? dropoff.address, 'Destination inconnue'),
    amount: toNumber(record.amount ?? record.final_price ?? record.estimated_price),
    vehicle: toStringValue(record.vehicle_type ?? record.vehicle, 'standard'),
    payment: toStringValue(record.payment_method ?? record.payment, 'cash'),
    distanceKm: toNumber(record.distance_km ?? record.distanceKm),
    durationMin: toNumber(record.duration_min ?? record.durationMin),
    createdAt: toStringValue(record.created_at ?? record.createdAt),
    rating: record.driver_rating !== undefined
      ? toNumber(record.driver_rating)
      : record.client_rating !== undefined
        ? toNumber(record.client_rating)
        : null,
  }
}

export function mapAdminRideDetail(value: unknown): AdminRideDetail {
  const record = asRecord(value)
  const pickup = asRecord(record.pickup)
  const dropoff = asRecord(record.dropoff)

  return {
    ...mapAdminRide(record),
    pickupLat: pickup.lat === undefined ? null : toNumber(pickup.lat),
    pickupLng: pickup.lng === undefined ? null : toNumber(pickup.lng),
    dropoffLat: dropoff.lat === undefined ? null : toNumber(dropoff.lat),
    dropoffLng: dropoff.lng === undefined ? null : toNumber(dropoff.lng),
    estimatedPrice: toNumber(record.estimated_price),
    finalPrice: toNumber(record.final_price ?? record.amount),
    cancellationCredit: toNumber(record.cancellation_credit),
    driverRating: record.driver_rating === undefined ? null : toNumber(record.driver_rating),
    clientRating: record.client_rating === undefined ? null : toNumber(record.client_rating),
    startedAt: toNullableString(record.started_at),
    completedAt: toNullableString(record.completed_at),
    cancelledAt: toNullableString(record.cancelled_at),
    cancelReason: toNullableString(record.cancel_reason),
    cancelledBy: toNullableString(record.cancelled_by),
    payments: asArray(record.payments).map((payment) => {
      const paymentRecord = asRecord(payment)

      return {
        id: toStringValue(paymentRecord.id),
        amount: toNumber(paymentRecord.amount),
        method: toStringValue(paymentRecord.method, 'cash'),
        status: toStringValue(paymentRecord.status, 'pending'),
        reference: toNullableString(paymentRecord.reference),
        createdAt: toStringValue(paymentRecord.created_at),
      }
    }),
  }
}

export function mapAdminClient(value: unknown): AdminClient {
  const record = asRecord(value)

  return {
    id: toStringValue(record.id),
    name: toStringValue(record.name, 'Client inconnu'),
    phone: toStringValue(record.phone, 'N/A'),
    email: toNullableString(record.email),
    avatar: toNullableString(record.avatar),
    status: normalizeClientStatus(record.status),
    walletBalance: toNumber(record.wallet_balance ?? record.walletBalance),
    totalRides: toNumber(record.total_rides ?? record.totalRides),
    totalSpent: toNumber(record.total_spent ?? record.totalSpent),
    createdAt: toStringValue(record.created_at ?? record.createdAt),
    // « Dernière activité » : date de la dernière course EN TANT QUE CLIENT
    // (calculée par UserController@index via withMax `last_client_ride_at`).
    lastRideAt: toNullableString(
      record.last_client_ride_at ?? record.last_ride_at ?? record.lastRideAt
    ),
  }
}

/** Dossier client complet (GET /admin/users/{id} → UserResource). */
export interface AdminClientDetail {
  id: string
  name: string
  phone: string
  email: string | null
  avatar: string | null
  status: ClientStatus
  isActive: boolean
  isBackoffice: boolean
  roles: string[]
  walletBalance: number
  totalRides: number
  totalSpent: number
  grossDriverRevenue: number
  averageRating: number
  createdAt: string
  driverProfile: {
    vehicleType: string
    vehicleMake: string
    vehicleModel: string
    vehicleYear: number
    vehiclePlate: string
    licenseNumber: string
    status: string
    isVerified: boolean
    totalEarnings: number
  } | null
}

export function mapAdminClientDetail(value: unknown): AdminClientDetail {
  const record = asRecord(value)
  const driverProfile = asRecord(record.driver_profile)

  return {
    id: toStringValue(record.id),
    name: toStringValue(record.name, 'Client inconnu'),
    phone: toStringValue(record.phone, 'N/A'),
    email: toNullableString(record.email),
    avatar: toNullableString(record.avatar),
    status: normalizeClientStatus(record.status),
    isActive: toBoolean(record.is_active),
    isBackoffice: toBoolean(record.is_backoffice),
    roles: asArray(record.roles).map((role) => toStringValue(role)).filter(Boolean),
    walletBalance: toNumber(record.wallet_balance ?? record.walletBalance),
    totalRides: toNumber(record.total_rides ?? record.totalRides),
    totalSpent: toNumber(record.total_spent ?? record.totalSpent),
    grossDriverRevenue: toNumber(record.gross_driver_revenue),
    averageRating: toNumber(record.average_rating ?? record.rating),
    createdAt: toStringValue(record.created_at ?? record.createdAt),
    driverProfile:
      record.driver_profile === undefined || record.driver_profile === null
        ? null
        : {
            vehicleType: toStringValue(driverProfile.vehicle_type, 'standard'),
            vehicleMake: toStringValue(driverProfile.vehicle_make),
            vehicleModel: toStringValue(driverProfile.vehicle_model),
            vehicleYear: toNumber(driverProfile.vehicle_year),
            vehiclePlate: toStringValue(driverProfile.plate_number, 'N/A'),
            licenseNumber: toStringValue(driverProfile.license_number),
            status: toStringValue(driverProfile.status),
            isVerified: toBoolean(driverProfile.is_verified),
            totalEarnings: toNumber(driverProfile.total_earnings),
          },
  }
}

export function mapAdminDriver(value: unknown): AdminDriver {
  const record = asRecord(value)
  const driverProfile = asRecord(record.driver_profile)

  return {
    id: toStringValue(record.id),
    name: toStringValue(record.name, 'Chauffeur inconnu'),
    phone: toStringValue(record.phone, 'N/A'),
    email: toNullableString(record.email),
    avatar: toNullableString(record.avatar),
    status: normalizeDriverStatus(record.driver_status ?? driverProfile.status, record.status),
    vehicleType: toStringValue(record.vehicle_type ?? driverProfile.vehicle_type, 'standard'),
    vehicleMake: toNullableString(record.vehicle_make ?? driverProfile.vehicle_make),
    vehiclePlate: toStringValue(record.plate_number ?? driverProfile.plate_number, 'N/A'),
    rating: toNumber(record.rating ?? record.average_rating),
    totalRides: toNumber(record.total_rides ?? record.totalRides),
    earnings: toNumber(record.total_earnings ?? record.earnings),
    commission: toNumber(record.commission),
    isVerified: toBoolean(record.is_verified ?? driverProfile.is_verified),
    createdAt: toStringValue(record.created_at ?? record.createdAt),
    // « Dernière activité » : date de la dernière course EN TANT QUE CHAUFFEUR
    // (calculée par UserController@index via withMax `last_driver_ride_at`).
    lastRideAt: toNullableString(
      record.last_driver_ride_at ?? record.last_ride_at ?? record.lastRideAt
    ),
  }
}

/**
 * Dossier chauffeur complet : compte, profil véhicule, localisation,
 * performance, finances (gains/commissions/versements), abonnement,
 * courses et avis récents.
 */
export function mapAdminDriverDetail(value: unknown): AdminDriverDetail {
  const record = asRecord(value)
  const profile = asRecord(record.driver_profile)
  const vehicle = asRecord(profile.vehicle)
  const location = asRecord(record.location)
  const performance = asRecord(record.performance)
  const rideCounts = asRecord(performance.rides)
  const finance = asRecord(record.finance)
  const payouts = asRecord(finance.payouts)
  const accountStatus = toStringValue(record.account_status, 'active')

  return {
    id: toStringValue(record.id),
    numericId: record.numeric_id === undefined ? null : toNumber(record.numeric_id),
    name: toStringValue(record.name, 'Chauffeur inconnu'),
    phone: toStringValue(record.phone, 'N/A'),
    email: toNullableString(record.email),
    avatar: toNullableString(record.avatar),
    roles: asArray(record.roles).map((role) => String(role)),
    accountStatus: accountStatus === 'suspended' ? 'suspended' : accountStatus === 'inactive' ? 'inactive' : 'active',
    isActive: toBoolean(record.is_active),
    isBackOffice: toBoolean(record.is_backoffice),
    createdAt: toStringValue(record.created_at),
    updatedAt: toNullableString(record.updated_at),

    profile: record.driver_profile == null ? null : {
      id: toStringValue(profile.id),
      licenseNumber: toStringValue(profile.license_number, 'N/A'),
      vehicleType: toStringValue(vehicle.type, 'standard'),
      vehicleMake: toStringValue(vehicle.make, 'N/A'),
      vehicleModel: toStringValue(vehicle.model, 'N/A'),
      vehicleYear: toNumber(vehicle.year),
      vehicleColor: toStringValue(vehicle.color, 'N/A'),
      vehiclePlate: toStringValue(vehicle.plate_number, 'N/A'),
      status: normalizeDriverStatus(profile.status, record.account_status),
      isVerified: toBoolean(profile.is_verified),
      totalEarnings: toNumber(profile.total_earnings),
      createdAt: toStringValue(profile.created_at),
      updatedAt: toNullableString(profile.updated_at),
    },

    location: {
      latitude: typeof location.latitude === 'number' ? location.latitude : null,
      longitude: typeof location.longitude === 'number' ? location.longitude : null,
      lastUpdatedAt: toNullableString(location.last_updated_at),
    },

    performance: {
      rating: toNumber(performance.rating),
      ratingCount: toNumber(performance.rating_count),
      totalRides: toNumber(rideCounts.total),
      completedRides: toNumber(rideCounts.completed),
      cancelledRides: toNumber(rideCounts.cancelled),
      inProgressRides: toNumber(rideCounts.in_progress),
      upcomingRides: toNumber(rideCounts.upcoming),
      completionRate: toNumber(performance.completion_rate),
      cancellationRate: toNumber(performance.cancellation_rate),
      totalDistanceKm: toNumber(performance.total_distance_km),
      totalDurationMin: toNumber(performance.total_duration_min),
      averageFare: toNumber(performance.average_fare),
      firstRideAt: toNullableString(performance.first_ride_at),
      lastRideAt: toNullableString(performance.last_ride_at),
    },

    finance: {
      walletBalance: toNumber(finance.wallet_balance),
      cancellationCredit: toNumber(finance.cancellation_credit),
      grossRevenue: toNumber(finance.gross_revenue),
      netEarnings: toNumber(finance.net_earnings),
      commission: toNumber(finance.commission),
      commissionRate: toNumber(finance.commission_rate),
      payouts: {
        total: toNumber(payouts.total),
        completed: toNumber(payouts.completed),
        pending: toNumber(payouts.pending),
        processing: toNumber(payouts.processing),
        failed: toNumber(payouts.failed),
        totalPaidOut: toNumber(payouts.total_paid_out),
        pendingAmount: toNumber(payouts.pending_amount),
        lastPayoutAt: toNullableString(payouts.last_payout_at),
      },
      recentPayouts: asArray(finance.recent_payouts).map((entry) => {
        const payout = asRecord(entry)
        return {
          id: toStringValue(payout.id),
          amount: toNumber(payout.amount),
          method: toStringValue(payout.method, 'N/A'),
          methodLabel: toStringValue(payout.method_label, 'N/A'),
          status: toStringValue(payout.status, 'pending'),
          statusLabel: toStringValue(payout.status_label, 'En attente'),
          reference: toNullableString(payout.reference),
          createdAt: toStringValue(payout.created_at),
          processedAt: toNullableString(payout.processed_at),
        }
      }),
    },

    subscription: record.subscription == null ? null : (() => {
      const subscription = asRecord(record.subscription)
      return {
        id: toStringValue(subscription.id),
        plan: toNullableString(subscription.plan),
        status: toStringValue(subscription.status, 'active'),
        startsAt: toNullableString(subscription.starts_at),
        endsAt: toNullableString(subscription.ends_at),
        freeRidesRemaining: toNumber(subscription.free_rides_remaining),
        isActive: toBoolean(subscription.is_active),
      }
    })(),

    recentRides: asArray(record.recent_rides).map((entry) => {
      const ride = asRecord(entry)
      const client = asRecord(ride.client)
      return {
        id: toStringValue(ride.id),
        status: normalizeRideStatus(ride.status),
        client: toStringValue(client.name, 'Client inconnu'),
        clientPhone: toNullableString(client.phone),
        from: toStringValue(ride.from ?? ride.pickup_address, '—'),
        to: toStringValue(ride.to ?? ride.dropoff_address, '—'),
        amount: toNumber(ride.amount),
        distanceKm: toNumber(ride.distance_km),
        durationMin: toNumber(ride.duration_min),
        payment: toStringValue(ride.payment_method, 'N/A'),
        clientRating: ride.client_rating == null ? null : toNumber(ride.client_rating),
        createdAt: toStringValue(ride.created_at),
        completedAt: toNullableString(ride.completed_at),
      }
    }),

    recentRatings: asArray(record.recent_ratings).map((entry) => {
      const rating = asRecord(entry)
      return {
        id: toStringValue(rating.id),
        score: toNumber(rating.score),
        comment: toNullableString(rating.comment),
        raterName: toNullableString(rating.rater_name),
        createdAt: toStringValue(rating.created_at),
      }
    }),
  }
}

export function mapFinanceOverview(value: unknown): FinanceOverviewData {
  const record = asRecord(value)
  const byMethod = asRecord(record.by_method ?? record.byMethod)

  return {
    totalRevenue: toNumber(record.total_revenue ?? record.totalRevenue),
    revenueGrowth: toNumber(record.revenue_growth ?? record.revenueGrowth),
    totalCommissions: toNumber(record.total_commissions ?? record.totalCommissions),
    commissionsGrowth: toNumber(record.commissions_growth ?? record.commissionsGrowth),
    pendingPayouts: toNumber(record.pending_payouts ?? record.pendingPayouts),
    walletBalance: toNumber(record.wallet_balance ?? record.walletBalance),
    byMethod: [
      { name: 'Wallet', value: toNumber(byMethod.wallet) },
      { name: 'Espèces', value: toNumber(byMethod.cash) },
      { name: 'Carte', value: toNumber(byMethod.card) },
      { name: 'Mobile Money', value: toNumber(byMethod.mobile_money ?? byMethod.mobileMoney) },
    ].filter((entry) => entry.value > 0),
  }
}

export function mapFinanceTransaction(value: unknown): FinanceTransaction {
  const record = asRecord(value)
  const user = asRecord(record.user)

  // L'API renvoie les rôles sous forme de TABLEAU (un utilisateur peut en
  // avoir plusieurs) ; un champ `role` simple n'existe pas côté backend.
  const userRoles = Array.isArray(user.roles)
    ? user.roles.map((r) => (r == null ? '' : String(r))).filter((r) => r !== '')
    : []
  const rawRole = userRoles[0] ?? toStringValue(user.role, 'unknown')

  return {
    id: toStringValue(record.id),
    type: 'ride_payment',
    amount: toNumber(record.amount),
    userId: user.id === undefined ? null : toStringValue(user.id),
    userName: toStringValue(user.name, 'Utilisateur inconnu'),
    userRole: rawRole === 'client' || rawRole === 'driver' || rawRole === 'admin' || rawRole === 'superadmin'
      ? rawRole
      : 'unknown',
    description: toStringValue(record.description, 'Paiement'),
    status: normalizePaymentStatus(record.status),
    createdAt: toStringValue(record.created_at ?? record.createdAt),
    reference: toStringValue(record.reference ?? record.transaction_id ?? record.transactionId, 'N/A'),
    method: toStringValue(record.method, 'cash'),
    rideId: record.ride_id === undefined ? null : toStringValue(record.ride_id),
  }
}

export function mapPendingPayout(value: unknown): PendingPayout {
  const record = asRecord(value)

  return {
    id: toStringValue(record.id),
    name: toStringValue(record.name, 'Chauffeur inconnu'),
    phone: toStringValue(record.phone, 'N/A'),
    amount: toNumber(record.total_earnings ?? record.amount),
    totalRides: toNumber(record.total_rides ?? record.totalRides),
    isVerified: toBoolean(record.is_verified ?? record.isVerified),
    status: toStringValue(record.status, 'offline'),
  }
}

export function mapOverviewReport(value: unknown): OverviewReport {
  const record = asRecord(value)
  const stats = asRecord(record.stats)

  return {
    stats: {
      totalRides: toNumber(stats.totalRides),
      totalRevenue: toNumber(stats.totalRevenue),
      totalDrivers: toNumber(stats.totalDrivers),
      totalUsers: toNumber(stats.totalUsers),
      avgRating: toNumber(stats.avgRating),
      cancelRate: toNumber(stats.cancelRate),
    },
    daily: asArray(record.daily).map((entry) => {
      const item = asRecord(entry)
      return {
        date: toStringValue(item.date),
        name: toStringValue(item.name),
        rides: toNumber(item.rides),
        revenue: toNumber(item.revenue),
      }
    }),
    ridesByType: asArray(record.ridesByType).map((entry) => {
      const item = asRecord(entry)
      return { name: toStringValue(item.name), value: toNumber(item.value) }
    }),
    topZones: asArray(record.topZones).map((entry) => {
      const item = asRecord(entry)
      return {
        name: toStringValue(item.name),
        value: toNumber(item.value),
        revenue: toNumber(item.revenue),
        avgDistanceKm: toNumber(item.avg_distance_km ?? item.avgDistanceKm),
        avgDurationMin: toNumber(item.avg_duration_min ?? item.avgDurationMin),
        cancelRate: toNumber(item.cancel_rate ?? item.cancelRate),
      }
    }),
    hourly: asArray(record.hourly).map((entry) => {
      const item = asRecord(entry)
      return { name: toStringValue(item.name), value: toNumber(item.value) }
    }),
  }
}

export function mapRevenueReport(value: unknown): RevenueReport {
  const record = asRecord(value)

  return {
    totalRevenue: toNumber(record.total_revenue ?? record.totalRevenue),
    totalCommission: toNumber(record.total_commission ?? record.totalCommission),
    netRevenue: toNumber(record.net_revenue ?? record.netRevenue),
    daily: asArray(record.daily).map((entry) => {
      const item = asRecord(entry)
      return {
        date: toStringValue(item.date),
        rides: toNumber(item.rides),
        revenue: toNumber(item.revenue),
        commission: toNumber(item.commission),
      }
    }),
    byMethod: asArray(record.by_method ?? record.byMethod).map((entry) => {
      const item = asRecord(entry)
      return {
        method: toStringValue(item.method),
        label: toStringValue(item.label ?? item.method),
        total: toNumber(item.total),
        count: toNumber(item.count),
      }
    }),
  }
}

export function mapDriverReport(value: unknown): DriverReport {
  const record = asRecord(value)

  return {
    totalDrivers: toNumber(record.total_drivers ?? record.totalDrivers),
    activeDrivers: toNumber(record.active_drivers ?? record.activeDrivers),
    inactiveDrivers: toNumber(record.inactive_drivers ?? record.inactiveDrivers),
    topDrivers: asArray(record.top_drivers ?? record.topDrivers).map((entry) => {
      const item = asRecord(entry)
      return {
        id: toNumber(item.id),
        name: toStringValue(item.name),
        phone: toStringValue(item.phone, 'N/A'),
        status: normalizeDriverStatus(item.status),
        completedRides: toNumber(item.completed_rides ?? item.completedRides),
        totalEarnings: toNumber(item.total_earnings ?? item.totalEarnings),
        isVerified: toBoolean(item.is_verified ?? item.isVerified),
        vehicleType: toNullableString(item.vehicle_type ?? item.vehicleType),
        createdAt: toStringValue(item.created_at ?? item.createdAt),
      }
    }),
  }
}
